import { useState } from "react";
import { LocalizedItemName } from "@/components/i18n/LocalizedItemName";
import { Link } from "@/components/i18n/LocaleLink";
import {
  ArrowRight,
  BookOpen,
  Calendar,
  CheckSquare,
  ChefHat,
  Cloud,
  Compass,
  Fish,
  Map,
  MapPin,
  PawPrint,
  Sparkles,
  Store,
} from "lucide-react";
import { buildRecipeCardData } from "@/features/recipes/recipe-data";
import { ROUTES } from "@/lib/routing";
import { buildResponsiveSrcSet } from "@/lib/responsive-image";
import { CurrencyIcon } from "@/components/ui/currency-icon";
import { MARKERS_NPCS } from "@/data/map/markers-npcs";
import { SHOPS_MARKERS } from "@/data/map/shops-markers";
import { ANIMALS_MARKERS } from "@/data/map/animals-markers";
import { animalCatalogId } from "@/lib/animal-ids";
import { getGuideBySlug } from "@/features/guides/registry";
import { useLocale } from "@/lib/i18n/LocaleContext";
import { useHeadTags } from "@/lib/seo/useHeadTags";

const FEATURED_GUIDE_SLUGS = [
  { slug: "how-to-fish", image: "/icons/fish/atlantic-salmon.webp", color: "from-sky-400 to-cyan-500" },
  { slug: "cooking-basics", image: "/icons/dish/grilled-mushroom.webp", color: "from-amber-400 to-orange-500" },
  { slug: "daily-checklist", image: "/icons/bird/amur-falcon.webp", color: "from-violet-400 to-purple-500" },
] as const;

const mapLayers = [
  { id: "npcs", data: MARKERS_NPCS } as const,
  { id: "shops", data: SHOPS_MARKERS } as const,
  { id: "animals", data: ANIMALS_MARKERS } as const,
];

function HeroMapPreview() {
  const { t } = useLocale();
  const [activeId, setActiveId] = useState<(typeof mapLayers)[number]["id"]>("npcs");

  const layerMeta = {
    npcs: { icon: MapPin, label: t("nav.npcs"), hint: t("home.hintNpcs") },
    shops: { icon: Store, label: t("nav.shops"), hint: t("home.hintShops") },
    animals: { icon: PawPrint, label: t("nav.animals"), hint: t("home.hintAnimals") },
  } as const;

  const active = mapLayers.find((layer) => layer.id === activeId) ?? mapLayers[0];
  const markers = active.data.slice(0, 9);
  const activeMeta = layerMeta[active.id];

  return (
    <div className="relative min-h-[520px] overflow-hidden rounded-[2.6rem] border-4 border-white bg-[#7fb0b2] shadow-[0_35px_90px_-15px_rgba(72,53,62,.4)] transition duration-500 hover:-translate-y-1">
      <div className="absolute inset-3 rounded-[2.1rem] border border-white/40" />
      <img src="/map.webp" alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(255,241,210,.45),transparent_25%),linear-gradient(135deg,rgba(48,31,39,.18),transparent_40%),linear-gradient(0deg,rgba(255,255,255,.12),rgba(255,255,255,.12))]" />
      <svg
        className="absolute inset-0 h-full w-full opacity-70"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M 14 78 Q 34 40 52 52 T 84 22"
          fill="none"
          stroke="#fff8ef"
          strokeWidth="0.7"
          strokeDasharray="2.6 2.4"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {markers.map((marker) => {
        const x = Math.max(7, Math.min(92, marker.position.lng / 10));
        const y = Math.max(8, Math.min(90, Math.abs(marker.position.lat) / 10));
        const image = marker.image ?? `/map/${marker.id}.webp`;
        return (
          <div
            key={`${active.id}-${marker.id}`}
            className="absolute flex size-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[3px] border-white bg-white shadow-lg shadow-black/15 transition duration-300"
            style={{ left: `${x}%`, top: `${y}%` }}
          >
            <img src={image} alt="" className="h-full w-full rounded-full object-cover p-0.5" />
          </div>
        );
      })}
      <div className="absolute left-5 top-5 inline-flex rotate-2 items-center gap-2 rounded-full border border-white/60 bg-[#fff8ef]/92 px-4 py-2 text-[11px] font-black uppercase tracking-[0.24em] text-[#6f5446] shadow-lg">
        <Sparkles className="size-3.5 text-[#e56b8d]" />
        {t("home.islandAtlas")}
      </div>
      <div className="absolute right-5 top-5 inline-flex items-center gap-1 rounded-full border border-white/60 bg-[#fff8ef]/92 p-1 shadow-lg">
        {mapLayers.map((layer) => {
          const meta = layerMeta[layer.id];
          const LayerIcon = meta.icon;
          const isActive = layer.id === active.id;
          return (
            <button
              key={layer.id}
              type="button"
              onClick={() => setActiveId(layer.id)}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.16em] transition ${
                isActive ? "bg-[#2d2528] text-white shadow-md" : "text-[#6f5446] hover:bg-white"
              }`}
            >
              <LayerIcon className="size-3" />
              {meta.label}
            </button>
          );
        })}
      </div>
      <div className="absolute bottom-5 left-5 right-5 rounded-[2rem] border border-white/70 bg-[#fffaf5]/92 p-5 shadow-xl backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.24em] text-[#d35f84]">{t("home.mapHeading")}</p>
            <p className="mt-1 text-lg font-bold text-[#433034]">
              {markers.length} {activeMeta.label.toLowerCase()} {t("home.mapNearby")}
            </p>
            <p className="mt-1 text-sm font-semibold text-[#82696c]">{activeMeta.hint}</p>
          </div>
          <Link
            to="/map"
            className="inline-flex items-center gap-2 rounded-full bg-[#2d2528] px-5 py-3 text-xs font-black uppercase tracking-[0.22em] text-[#fff7ef] shadow-lg transition hover:bg-[#1f191c]"
          >
            {t("home.openMap")} <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function HeroSection() {
  const { t } = useLocale();

  const heroStats = [
    { label: t("home.statCatalogued"), value: "800+" },
    { label: t("home.statLayers"), value: "6" },
    { label: t("home.statFree"), value: "100%" },
  ];

  const mainSections = [
    {
      title: t("nav.npcs"),
      path: "/npcs",
      icon: MapPin,
      gradient: "from-rose-400 to-pink-500",
      glow: "rgba(251,113,133,.18)",
    },
    {
      title: t("nav.fish"),
      path: "/fish",
      icon: Fish,
      gradient: "from-sky-400 to-cyan-500",
      glow: "rgba(56,189,248,.16)",
    },
    {
      title: t("nav.recipes"),
      path: "/recipes",
      icon: ChefHat,
      gradient: "from-amber-400 to-orange-500",
      glow: "rgba(251,146,60,.16)",
    },
    {
      title: t("nav.tracker"),
      path: "/tracker",
      icon: CheckSquare,
      gradient: "from-purple-400 to-indigo-500",
      glow: "rgba(167,139,250,.16)",
    },
    {
      title: t("nav.animals"),
      path: "/animals",
      icon: PawPrint,
      gradient: "from-violet-400 to-purple-500",
      glow: "rgba(139,92,246,.16)",
    },
    {
      title: t("nav.shops"),
      path: "/shops",
      icon: Store,
      gradient: "from-pink-400 to-rose-500",
      glow: "rgba(244,114,182,.18)",
    },
    {
      title: t("nav.locations"),
      path: "/locations",
      icon: Calendar,
      gradient: "from-emerald-400 to-teal-500",
      glow: "rgba(52,211,153,.18)",
    },
    {
      title: t("nav.map"),
      path: "/map",
      icon: Cloud,
      gradient: "from-slate-400 to-blue-400",
      glow: "rgba(100,116,139,.18)",
    },
  ];

  return (
    <>
      <section className="relative rounded-[3rem] border border-[#f2d6c2] bg-[#fffaf4] p-5 shadow-[0_30px_90px_-20px_rgba(91,61,52,.3)] md:p-8 lg:p-12">
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[3rem]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,213,173,.55),transparent_28%),radial-gradient(circle_at_75%_12%,rgba(246,129,172,.24),transparent_26%)]" />
          <div className="absolute inset-0 opacity-[0.55] [background-image:radial-gradient(rgba(178,120,90,.16)_1px,transparent_1px)] [background-size:18px_18px]" />
          <div className="absolute -left-20 -top-24 size-72 rounded-full bg-[#ffcf9e] opacity-40 blur-3xl" />
          <div className="absolute -bottom-28 right-0 size-80 rounded-full bg-[#f6a8c4] opacity-25 blur-3xl" />
          <div className="absolute inset-y-0 left-[44%] hidden w-px bg-[linear-gradient(180deg,transparent,rgba(122,88,74,.14),transparent)] lg:block" />
        </div>

        <div className="relative grid items-center gap-14 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="inline-flex -rotate-2 items-center gap-2 rounded-full border border-[#f0d2bd] bg-white px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.24em] text-[#b3673a] shadow-sm">
                <Compass className="size-3.5" />
                {t("home.badge")}
              </div>
              <h1 className="text-5xl font-extrabold leading-[0.95] text-[#2b1f23] md:text-7xl xl:text-[5.6rem]">
                {t("home.titleStop")} <span className="italic text-[#d3703f]">{t("home.titleWandering")}</span>
                <br />
                {t("home.titleStart")}
              </h1>
              <p className="max-w-xl text-lg font-medium leading-8 text-[#715c5e] md:text-xl">
                {t("home.description")}
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/map"
                className="inline-flex h-14 items-center justify-center gap-3 rounded-full bg-[#2d2528] px-7 text-sm font-black uppercase tracking-[0.22em] text-[#fff8f1] shadow-xl shadow-[#8c6b65]/25 transition hover:-translate-y-0.5 hover:bg-[#1f191c]"
              >
                <Map className="size-4" /> {t("home.openAtlas")} <ArrowRight className="size-4" />
              </Link>
              <Link
                to="/tracker"
                className="inline-flex h-14 items-center justify-center gap-3 rounded-full border border-[#dcc4b7] bg-white/80 px-7 text-sm font-black uppercase tracking-[0.22em] text-[#5d474a] shadow-sm transition hover:-translate-y-0.5 hover:bg-white"
              >
                <CheckSquare className="size-4" /> {t("home.tracker")}
              </Link>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              {heroStats.map((stat, index) => (
                <div
                  key={stat.label}
                  className={`rounded-2xl border border-dashed border-[#e3c8b6] bg-white/70 px-4 py-2.5 shadow-sm ${
                    index % 2 === 0 ? "-rotate-1" : "rotate-1"
                  }`}
                >
                  <p className="text-xl font-extrabold text-[#2b1f23]">{stat.value}</p>
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#a48d87]">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <HeroMapPreview />
            <Link
              to="/npcs"
              className="group absolute -left-11 top-1/2 hidden w-36 -translate-y-1/2 -rotate-3 rounded-[1.3rem] border-4 border-white bg-white p-2 shadow-2xl transition duration-300 hover:rotate-0 hover:scale-105 lg:block"
            >
              <div className="aspect-square overflow-hidden bg-[#ffe3df]">
                <img src="/map/atara.webp" alt="" className="h-full w-full object-cover" />
              </div>
              <p className="mt-1.5 text-center text-[10px] font-black uppercase tracking-wider text-[#8a7268]">
                {t("home.meetLocals")}
              </p>
            </Link>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
        {mainSections.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className="group relative overflow-hidden rounded-[1.7rem] border border-white/70 bg-[#fffaf6]/88 p-4 shadow-lg shadow-[#ead7cb]/45 transition hover:-translate-y-1 hover:shadow-xl"
            >
              <div
                className="absolute inset-0 opacity-0 transition group-hover:opacity-100"
                style={{ background: `radial-gradient(circle at 25% 25%, ${item.glow}, transparent 70%)` }}
              />
              <div
                className={`relative mb-3 flex size-12 items-center justify-center rounded-2xl bg-gradient-to-br ${item.gradient} shadow-lg transition group-hover:rotate-[-8deg] group-hover:scale-110`}
              >
                <Icon className="size-5 text-white" />
              </div>
              <div className="relative">
                <p className="text-sm font-extrabold text-[#3b2e33]">{item.title}</p>
                <p className="mt-1 inline-flex translate-y-1 items-center gap-1 text-[10px] font-black uppercase tracking-[0.22em] text-[#9c8883] opacity-0 transition group-hover:translate-y-0 group-hover:opacity-100">
                  {t("home.explore")} <ArrowRight className="size-3" />
                </p>
              </div>
            </Link>
          );
        })}
      </div>
    </>
  );
}

function RecipesSection() {
  const { locale, t } = useLocale();
  const cards = buildRecipeCardData(locale)
    .filter((card) => card.image)
    .slice(0, 4);

  return (
    <section className="space-y-8">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#ffe8bf] px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#b46b1d]">
            <ChefHat className="size-4" /> {t("home.recipesPill")}
          </div>
          <h2 className="mt-4 text-4xl font-extrabold leading-tight text-[#2b1f23] md:text-6xl">
            {t("home.recipesTitle")}
          </h2>
          <p className="mt-3 max-w-xl text-lg font-medium text-[#715c5e]">{t("home.recipesDescription")}</p>
        </div>
        <Link
          to="/recipes"
          className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[#2d2528] px-7 text-sm font-black uppercase tracking-[0.22em] text-[#fff8f1] shadow-xl"
        >
          {t("home.viewRecipes")} <ArrowRight className="size-4" />
        </Link>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <Link
            key={card.id}
            to={ROUTES.collectionDetail("dish", card.id)}
            className="group relative overflow-hidden rounded-[2rem] border border-white/80 bg-[#fffaf5] p-5 shadow-xl shadow-[#f0ddc8]/70 transition hover:-translate-y-1.5 hover:shadow-2xl"
          >
            {card.profit != null && card.profit > 0 && (
              <div className="absolute right-4 top-4 z-10 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-white shadow-md">
                +{card.profit} {t("home.profit")}
              </div>
            )}
            <div className="relative aspect-square overflow-hidden rounded-[1.5rem] bg-gradient-to-br from-[#fff1d8] to-[#ffe3bd] p-8 shadow-inner">
              <div className="absolute inset-0 opacity-60 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,.7),transparent_55%)]" />
              <img
                src={card.image ?? ""}
                srcSet={buildResponsiveSrcSet(card.image ?? "")}
                sizes="(min-width: 640px) 220px, 45vw"
                alt=""
                className="relative h-full w-full object-contain drop-shadow-md transition group-hover:scale-110 group-hover:rotate-2"
              />
            </div>
            <h3 className="mt-4 line-clamp-2 text-xl font-extrabold leading-tight text-[#2b1f23]">
              <LocalizedItemName itemId={card.id} />
            </h3>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <p className="inline-flex items-center gap-1.5 rounded-full bg-[#fff1d8] px-2.5 py-1 text-sm font-black text-[#b46b1d]">
                <CurrencyIcon currency="GOLD" size={15} />
                {card.sellPrice || "--"}
              </p>
              <p className="inline-flex items-center gap-1 rounded-full bg-[#f4ece3] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-[#8a7268]">
                {card.ingredients.length} {t("home.ingr")}
              </p>
              <p className="inline-flex items-center gap-1 rounded-full bg-[#f4ece3] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-[#8a7268]">
                {card.tool.charAt(0) + card.tool.slice(1).toLowerCase()}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function AnimalsSection() {
  const { locale, t } = useLocale();

  return (
    <section className="relative">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#ffe3df] px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#c86a59]">
            <PawPrint className="size-4" /> {t("home.animalsPill")}
          </div>
          <h2 className="mt-4 text-4xl font-extrabold leading-tight text-[#2b1f23] md:text-6xl">
            {t("home.animalsTitle")}
          </h2>
          <p className="mt-3 max-w-xl text-lg font-medium text-[#715c5e]">{t("home.animalsDescription")}</p>
        </div>
        <Link
          to="/animals"
          className="inline-flex h-14 items-center justify-center gap-2 rounded-full bg-[#2d2528] px-7 text-sm font-black uppercase tracking-[0.22em] text-[#fff8f1] shadow-xl"
        >
          {t("home.viewAnimals")} <ArrowRight className="size-4" />
        </Link>
      </div>

      <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {ANIMALS_MARKERS.slice(0, 6).map((animal, index) => (
          // The map's ids are not the catalog's for 3 of these — see lib/animal-ids.ts. Linking
          // the raw marker id sent a third of this row to /animals/rabbit, a real 404.
          <Link key={animal.id} to={`/animals/${animalCatalogId(animal.id)}`} className="group">
            <div className="relative mb-4 aspect-[4/5] overflow-hidden rounded-[2rem] border border-white/80 bg-[#fffaf5] shadow-xl transition group-hover:-translate-y-2">
              <div
                className={`absolute inset-0 opacity-25 bg-gradient-to-br ${index % 2 === 0 ? "from-rose-200 to-orange-200" : "from-blue-200 to-purple-200"}`}
              />
              <img
                src={animal.image}
                srcSet={buildResponsiveSrcSet(animal.image)}
                sizes="(min-width: 640px) 217px, 40vw"
                alt=""
                className="relative h-full w-full object-contain p-7 transition group-hover:scale-110"
              />
            </div>
            <h3 className="text-center text-sm font-extrabold capitalize text-[#5d474a] group-hover:text-rose-600">
              <LocalizedItemName itemId={animalCatalogId(animal.id)} className={locale === "en" ? "capitalize" : ""} />
            </h3>
          </Link>
        ))}
      </div>
    </section>
  );
}

function GuidesSection() {
  const { t, locale } = useLocale();
  const guides = FEATURED_GUIDE_SLUGS.map((entry) => ({
    ...entry,
    guide: getGuideBySlug(locale, entry.slug),
  })).filter((entry): entry is typeof entry & { guide: NonNullable<typeof entry.guide> } => Boolean(entry.guide));

  return (
    <section className="overflow-hidden rounded-[3rem] bg-[#2d2528] p-8 shadow-2xl shadow-[#2d2528]/35 md:p-12">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/6 px-4 py-2 text-xs font-black uppercase tracking-[0.22em] text-[#f0c49b]">
            <BookOpen className="size-4" /> {t("home.guidesPill")}
          </div>
          <h2 className="mt-4 text-4xl font-extrabold leading-tight text-white md:text-6xl">{t("home.guidesTitle")}</h2>
          <p className="mt-3 max-w-lg text-base font-medium text-[#d9c3b8]/85">{t("home.guidesDescription")}</p>
        </div>
        <Link
          to="/guides"
          className="inline-flex h-12 w-fit items-center justify-center gap-2 rounded-full border border-white/15 bg-white/10 px-6 text-xs font-black uppercase tracking-[0.22em] text-white transition hover:bg-white/15"
        >
          {t("home.allGuides")} <ArrowRight className="size-3.5" />
        </Link>
      </div>

      <div className="mt-10 grid gap-5 md:grid-cols-3">
        {guides.map(({ slug, image, color, guide }) => (
          <Link
            key={slug}
            to={`/guides/${guide.meta.slug}`}
            className="group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 backdrop-blur transition hover:-translate-y-1.5 hover:bg-white/10"
          >
            <div
              className={`relative flex h-40 items-center justify-center overflow-hidden bg-gradient-to-br ${color}`}
            >
              <div className="absolute inset-0 opacity-50 bg-[radial-gradient(circle_at_28%_22%,rgba(255,255,255,.55),transparent_60%)]" />
              <img
                src={image}
                srcSet={buildResponsiveSrcSet(image)}
                sizes="96px"
                alt=""
                className="relative h-24 w-24 object-contain drop-shadow-xl transition duration-300 group-hover:scale-110 group-hover:rotate-[-4deg]"
              />
            </div>
            <div className="p-5">
              <h3 className="text-lg font-extrabold text-white">{guide.meta.title}</h3>
              <p className="mt-1.5 line-clamp-2 text-sm font-medium text-[#d9c3b8]/85">{guide.meta.summary}</p>
              <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-[0.2em] text-[#e8935a]">
                {t("home.exploreGuide")} <ArrowRight className="size-3.5 transition group-hover:translate-x-1" />
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLocale();
  useHeadTags({
    title: `${t("home.titleStop")} ${t("home.titleWandering")} ${t("home.titleStart")}`,
    description: t("home.description"),
    path: "/",
  });

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_15%_10%,rgba(255,208,171,.28),transparent_22%),radial-gradient(circle_at_78%_16%,rgba(244,144,181,.18),transparent_18%),radial-gradient(circle_at_84%_72%,rgba(164,214,229,.18),transparent_20%),linear-gradient(180deg,#fffaf3,#fff8f0_38%,#fff6fb)]" />
      <div className="mx-auto flex max-w-[1400px] flex-col gap-24 px-4 py-8">
        <HeroSection />
        <RecipesSection />
        <AnimalsSection />
        <GuidesSection />
      </div>
    </div>
  );
}
